/** Categories aligned with scripts/config/chat-skills/pilot-feedback/SKILL.md */
export type ChatFeedbackCategory = 'bug' | 'feedback' | 'agent-issue' | 'question'

export const PILOT_FEEDBACK_SKILL_ID = 'pilot-feedback'

export const CHAT_FEEDBACK_OPEN_EVENT = 'flownotes:open-chat-feedback'

export type ChatFeedbackOpenDetail = {
  category?: ChatFeedbackCategory
  note?: string
  conversationId?: string | null
  threadId?: string | null
  model?: string | null
  provider?: string | null
}

export function chatFeedbackOpenEventName(): string {
  return CHAT_FEEDBACK_OPEN_EVENT
}

/**
 * User-facing landing label for pilot feedback.
 * Tickets always land in FlowNotes Product - never show raw content_root (esp. ws/{hex}/).
 */
export const PILOT_FEEDBACK_LANDING_DISPLAY_LABEL = 'FlowNotes Product / pilot-feedback'

/** @deprecated Use PILOT_FEEDBACK_LANDING_DISPLAY_LABEL - kept for prototype fallbacks */
export const PILOT_FEEDBACK_FOLDER_FALLBACK_LABEL = PILOT_FEEDBACK_LANDING_DISPLAY_LABEL

/**
 * Display label for where pilot feedback will be filed.
 * Ignores active-workspace contentRoot - landing is always FlowNotes Product.
 * Never returns a ws/{hex} storage path.
 */
export function buildPilotFeedbackFolderLabel(
  _contentRoot?: string | null | undefined,
): string {
  return PILOT_FEEDBACK_LANDING_DISPLAY_LABEL
}

/** True when a path is an internal cloud storage root (must not be shown to users). */
export function isInternalWsStoragePath(path: string | null | undefined): boolean {
  if (!path) return false
  return /^ws\/[a-f0-9]{32}\b/i.test(String(path).replace(/^\/+/, ''))
}

/**
 * Prefer a slug-shaped display path; if the only value is ws/{hex}/..., fall back to
 * the product landing label + filename so users never see internal storage roots.
 */
export function formatPilotFeedbackPathForDisplay(
  filePath: string | null | undefined,
  slugShapedPath?: string | null,
): string {
  const preferred = (slugShapedPath || filePath || '').trim()
  if (!preferred) return PILOT_FEEDBACK_LANDING_DISPLAY_LABEL
  if (isInternalWsStoragePath(preferred)) {
    const base = preferred.split('/').pop() || 'ticket.md'
    return `${PILOT_FEEDBACK_LANDING_DISPLAY_LABEL}/${base}`
  }
  return preferred.replace(/\/+$/, '')
}

/** Open chat feedback flow (FeedbackModal listens via CHAT_FEEDBACK_OPEN_EVENT). */
export function openChatFeedback(detail: ChatFeedbackOpenDetail = {}): void {
  window.dispatchEvent(
    new CustomEvent(CHAT_FEEDBACK_OPEN_EVENT, {
      detail,
    }),
  )
}

export function buildChatFeedbackDraft(detail: ChatFeedbackOpenDetail = {}): string {
  const category = detail.category ?? 'feedback'
  const lines: string[] = [
    `I'd like to file pilot feedback (category: ${category}).`,
    '',
    'What happened:',
    detail.note?.trim() || '[describe the issue, unexpected behavior, or idea here]',
  ]

  const context: string[] = []
  if (detail.conversationId) context.push(`conversationId: ${detail.conversationId}`)
  if (detail.threadId) context.push(`threadId: ${detail.threadId}`)
  if (detail.model) {
    const provider = detail.provider?.trim()
    context.push(`model: ${provider ? `${provider}/` : ''}${detail.model}`)
  }

  if (context.length > 0) {
    lines.push('', 'Session context (for diagnostics):', ...context.map((c) => `- ${c}`))
  }

  lines.push(
    '',
    'Expected (if applicable):',
    '',
    'Actual (if applicable):',
  )

  return lines.join('\n')
}
